"use client";

import { useEffect, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { formatDateTime } from "@/lib/format-number";
import { t } from "@/lib/i18n/interpolate";
import {
  earthCalendarAge,
  type PlanetAge,
  planetAges,
} from "@/lib/tool-kit/astronomy/planet-age";
import {
  dayNumber,
  nowInZone,
  parseIsoDay,
} from "@/lib/tool-kit/astronomy/zone";

/** English strings. A translated content file overrides these through `ui`. */
export const AGE_ON_OTHER_PLANETS_CALCULATOR_STRINGS = {
  birth: "Date of birth",
  asOf: "Age on this date",
  today: "Today",
  show: "Show the headline for",
  names: {
    mercury: "Mercury",
    venus: "Venus",
    earth: "Earth",
    mars: "Mars",
    jupiter: "Jupiter",
    saturn: "Saturn",
    uranus: "Uranus",
    neptune: "Neptune",
    pluto: "Pluto",
  },
  invalidBirth: "Enter a valid date of birth.",
  invalidAsOf: "Enter a valid date to find the age on.",
  order: "The birth date comes after the date you are finding the age on.",
  tooEarly: "Enter a birth date from the year 1000 onward.",
  results: "Your age on {planet}",
  years: "{value} years",
  headline: "{planet} years old",
  birthdays: "Birthdays so far",
  daysLived: "Days lived",
  yearLength: "One {planet} year",
  yearLengthValue: "{days} Earth days",
  earthAge: "Your age on Earth",
  earthAgeValue: "{years} y {months} m {days} d",
  tableHeading: "Your age on every planet",
  planetColumn: "Planet",
  periodColumn: "Year length (Earth days)",
  ageColumn: "Your age (years)",
  birthdaysColumn: "Birthdays",
  nextColumn: "Next birthday",
  nextValue: "{date} (in {days} days)",
  nextTomorrow: "{date} (tomorrow)",
  copy: "Copy result",
  copied: "Copied!",
  summary: "Born {birth}: {age} years old on {planet} on {asOf}.",
  note: "Each planet's year is its sidereal orbit period from NASA's Planetary Fact Sheets, and Earth years are 365.2425 days. Pluto is a dwarf planet. Birthdays on other planets are rounded to the nearest whole day, and the time of day you were born is ignored.",
} as const;

type Strings = typeof AGE_ON_OTHER_PLANETS_CALCULATOR_STRINGS;

const MS_PER_DAY = 86_400_000;
const MIN_YEAR = 1000;

function dateText(locale: string, day: number): string {
  return formatDateTime(locale, new Date(day * MS_PER_DAY), {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function Table({
  ages,
  locale,
  pick,
  strings,
}: {
  ages: PlanetAge[];
  locale: string;
  pick: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const names = strings.names as Record<string, string>;
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.planetColumn}
            </th>
            <th className={TH} scope="col">
              {strings.periodColumn}
            </th>
            <th className={TH} scope="col">
              {strings.ageColumn}
            </th>
            <th className={TH} scope="col">
              {strings.birthdaysColumn}
            </th>
            <th className={TH} scope="col">
              {strings.nextColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          {ages.map((row) => (
            <tr className={row.id === pick ? "bg-primary/10" : ""} key={row.id}>
              <td className={`${TD} font-medium`}>{names[row.id]}</td>
              <td className={TD}>
                {fmt(row.periodDays, { maximumFractionDigits: 4 })}
              </td>
              <td className={TD}>
                {fmt(row.age, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
              <td className={TD}>{fmt(row.birthdays)}</td>
              <td className={TD}>
                {t(
                  row.daysUntil === 1
                    ? strings.nextTomorrow
                    : strings.nextValue,
                  {
                    date: dateText(locale, row.nextBirthday),
                    days: fmt(row.daysUntil),
                  }
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AgeOnOtherPlanetsCalculatorWidget() {
  const strings: Strings = useToolUi(AGE_ON_OTHER_PLANETS_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [birth, setBirth] = useState("2000-01-01");
  const [asOfText, setAsOfText] = useState("2026-10-16");
  const [pick, setPick] = useState("mars");

  // Start from today's date once the page is on the reader's machine.
  useEffect(() => {
    setAsOfText(
      nowInZone(new Intl.DateTimeFormat().resolvedOptions().timeZone).date
    );
  }, []);

  const born = parseIsoDay(birth);
  const asOfDay = parseIsoDay(asOfText);
  const birthNumber =
    born === null ? null : dayNumber(born.year, born.month, born.day);
  const asOfNumber =
    asOfDay === null
      ? null
      : dayNumber(asOfDay.year, asOfDay.month, asOfDay.day);
  let problem: string | null = null;
  if (born === null || birthNumber === null) {
    problem = strings.invalidBirth;
  } else if (asOfNumber === null) {
    problem = strings.invalidAsOf;
  } else if (born.year < MIN_YEAR) {
    problem = strings.tooEarly;
  } else if (birthNumber > asOfNumber) {
    problem = strings.order;
  }
  const names = strings.names as Record<string, string>;
  const ready = problem === null && birthNumber !== null && asOfNumber !== null;
  const ages = ready ? planetAges(birthNumber, asOfNumber) : [];
  const chosen = ages.find((row) => row.id === pick);
  const earth = ready ? earthCalendarAge(birthNumber, asOfNumber) : null;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <TextField
          id="ap-birth"
          label={strings.birth}
          onChange={setBirth}
          type="date"
          value={birth}
        />
        <TextField
          id="ap-asof"
          label={strings.asOf}
          onChange={setAsOfText}
          type="date"
          value={asOfText}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setAsOfText(
              nowInZone(new Intl.DateTimeFormat().resolvedOptions().timeZone)
                .date
            )
          }
          type="button"
        >
          {strings.today}
        </button>
      </div>
      <div aria-live="polite" className="space-y-6">
        {problem === null ? null : <ErrorNotice>{problem}</ErrorNotice>}
        {ready &&
        chosen &&
        earth &&
        birthNumber !== null &&
        asOfNumber !== null ? (
          <>
            <section className="space-y-4">
              <SelectField
                id="ap-pick"
                label={strings.show}
                onChange={setPick}
                options={ages.map((row) => ({
                  value: row.id,
                  label: names[row.id] ?? row.id,
                }))}
                value={pick}
              />
              <h2 className={WIDGET_H2}>
                {t(strings.results, { planet: names[pick] ?? pick })}
              </h2>
              <StatGrid>
                <StatCard
                  emphasis
                  label={t(strings.headline, { planet: names[pick] ?? pick })}
                  value={fmt(chosen.age, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                />
                <StatCard
                  label={strings.birthdays}
                  value={fmt(chosen.birthdays)}
                />
                <StatCard
                  label={t(strings.yearLength, { planet: names[pick] ?? pick })}
                  value={t(strings.yearLengthValue, {
                    days: fmt(chosen.periodDays, { maximumFractionDigits: 3 }),
                  })}
                />
                <StatCard
                  label={strings.earthAge}
                  value={t(strings.earthAgeValue, {
                    years: fmt(earth.years),
                    months: fmt(earth.months),
                    days: fmt(earth.days),
                  })}
                />
                <StatCard
                  label={strings.daysLived}
                  value={fmt(asOfNumber - birthNumber)}
                />
              </StatGrid>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                text={t(strings.summary, {
                  birth: dateText(locale, birthNumber),
                  age: fmt(chosen.age, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                  planet: names[pick] ?? pick,
                  asOf: dateText(locale, asOfNumber),
                })}
              />
            </section>
            <section className={`${CARD} space-y-3`}>
              <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
              <Table
                ages={ages}
                locale={locale}
                pick={pick}
                strings={strings}
              />
            </section>
          </>
        ) : null}
      </div>
      <Notice>{strings.note}</Notice>
    </div>
  );
}
